import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableHead, TableRow, Chip, CircularProgress, Alert,
  TablePagination, TextField, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import api from '../../api';

const HoldSalaryReport = () => {
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });

  // Pagination, Search, & Filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState('All');
  const [filterStatus, setFilterStatus] = useState('Hold Salary'); 

  // Release Dialog State
  const [releaseDialog, setReleaseDialog] = useState({ open: false, holdId: null, reason: '' });
  const [releaseLoading, setReleaseLoading] = useState(false);

  const fetchHolds = async () => {
    try {
      setLoading(true);
      const res = await api.get('/payroll/hold-salary-report'); 
      setHolds(res.data || []);
    } catch (err) {
      showNotif('Failed to load Hold Salary report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolds(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleOpenReleaseDialog = (holdId) => {
    setReleaseDialog({ open: true, holdId, reason: '' });
  };

  const handleCloseReleaseDialog = () => {
    setReleaseDialog({ open: false, holdId: null, reason: '' });
  };

  const submitRelease = async () => {
    if (!releaseDialog.reason.trim()) {
      showNotif('Please enter a reason for releasing the salary.', 'error');
      return;
    }
    
    try {
      setReleaseLoading(true);
      await api.post(`/payroll/hold-salary/${releaseDialog.holdId}/release`, {
        reason: releaseDialog.reason
      });
      showNotif('Salary successfully released.');
      handleCloseReleaseDialog();
      fetchHolds(); // Refresh the grid
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to release salary.', 'error');
    } finally {
      setReleaseLoading(false);
    }
  };

  const filteredHolds = holds.filter(h => {
    const matchSearch = h.emp_id.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        h.emp_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchSource = filterSource === 'All' || h.source === filterSource;
    const matchStatus = filterStatus === 'All' || h.status === filterStatus;
    return matchSearch && matchSource && matchStatus;
  });

  const paginatedHolds = filteredHolds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>Hold Salary & Release Audit</Typography>
            <Typography variant="body2" color="textSecondary">View tracking history for auto and manual salary holds.</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField 
              size="small" label="Search Emp ID / Name" 
              value={searchQuery} onChange={(e) => {setSearchQuery(e.target.value); setPage(0);}} 
              sx={{ width: 200 }} 
            />
            <TextField 
              select size="small" label="Source" 
              value={filterSource} onChange={(e) => {setFilterSource(e.target.value); setPage(0);}} 
              sx={{ width: 120 }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Auto">Auto</MenuItem>
              <MenuItem value="Manual">Manual</MenuItem>
            </TextField>
            <TextField 
              select size="small" label="Status" 
              value={filterStatus} onChange={(e) => {setFilterStatus(e.target.value); setPage(0);}} 
              sx={{ width: 150 }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Hold Salary">Hold Salary</MenuItem>
              <MenuItem value="Released">Released</MenuItem>
            </TextField>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date Info</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Action By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source / Reason</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedHolds.map((h) => (
                  <TableRow key={h.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{h.emp_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{h.emp_id}</Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">Hold: <strong>{h.hold_date}</strong></Typography>
                      <Typography variant="caption" color="textSecondary">Release: {h.released_date || '--'}</Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">Hold By: <strong>{h.hold_by}</strong></Typography>
                      <Typography variant="caption" color="textSecondary">Release By: {h.released_by}</Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 250 }}>
                      <Chip label={h.source} size="small" sx={{ mb: 0.5, height: 20, fontSize: '0.7rem' }} />
                      <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {h.reason}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip label={h.status} size="small" color={h.status === 'Hold Salary' ? 'error' : 'success'} />
                    </TableCell>

                    <TableCell align="center">
                      <Button 
                        variant="outlined" 
                        size="small" 
                        color="success" 
                        disabled={h.status !== 'Hold Salary'}
                        onClick={() => handleOpenReleaseDialog(h.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Release
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={filteredHolds.length} page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      <Dialog open={releaseDialog.open} onClose={handleCloseReleaseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Release Hold Salary</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Provide a mandatory reason for releasing this salary hold. This action will be permanently recorded in the audit logs.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Release Reason"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={releaseDialog.reason}
            onChange={(e) => setReleaseDialog({ ...releaseDialog, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReleaseDialog} color="inherit" disabled={releaseLoading}>
            Cancel
          </Button>
          <Button onClick={submitRelease} variant="contained" color="success" disabled={releaseLoading}>
            {releaseLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Release'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HoldSalaryReport;